<script lang="ts">
  import {
    Kanban,
    Wrench,
    Mail,
    FileText,
    CheckSquare,
    Phone,
    Calendar,
    ArrowDownLeft,
    ArrowUpRight,
    MapPin
  } from 'lucide-svelte';
  import { cleanEmailPreview } from '$lib/utils/emailPreview';
  import type { TimelineEntry } from '$lib/utils/timeline';
  import type { Activity, ActivityType } from '$lib/types';

  // Unified activity+issue feed with type filter chips — the site
  // dashboard's timeline (see the sites revamp) and the owner dashboard's
  // cross-site rollup feed both render through this, so entry styling never
  // drifts between the two. Where the two pages genuinely differ (site vs.
  // owner direction heuristics, whether a site-attribution chip means
  // anything) that stays a prop rather than being guessed in here.
  const {
    entries,
    emailDirection,
    onOpenIssue,
    onOpenEmail,
    showSiteChips = false,
    onOpenSite,
    emptyLabel = 'Nothing logged yet.'
  }: {
    entries: TimelineEntry[];
    emailDirection: (a: Activity) => 'received' | 'sent';
    onOpenIssue: (issueId: number) => void;
    onOpenEmail: (a: Activity) => void;
    // Owner rollup only (problem 8): each entry's originating site as a
    // clickable chip, since a rollup spans several sites at once — a
    // single-site dashboard has nothing to attribute to and leaves this off.
    showSiteChips?: boolean;
    onOpenSite?: (siteId: number) => void;
    emptyLabel?: string;
  } = $props();

  const ISSUE_STATUS_BADGE: Record<string, { bg: string; fg: string }> = {
    open: { bg: '#ECF8CC', fg: '#5c7a00' },
    in_progress: { bg: '#FBF0DC', fg: '#8a5c00' },
    resolved: { bg: '#E6E9EE', fg: '#10223A' }
  };
  const ACTIVITY_BADGE: Record<string, { bg: string; fg: string }> = {
    note: { bg: '#E3F0F1', fg: '#226A73' },
    task: { bg: '#E6E9EE', fg: '#10223A' },
    cold_call: { bg: '#FBF0DC', fg: '#8a5c00' },
    call: { bg: '#FBF0DC', fg: '#8a5c00' },
    meeting: { bg: '#ECF8CC', fg: '#5c7a00' },
    email: { bg: '#EAE6F3', fg: '#5a4b8a' }
  };
  const ACTIVITY_ICON: Record<ActivityType, typeof FileText> = {
    whatsapp: Phone,
    visit: Calendar,
    note: FileText,
    task: CheckSquare,
    cold_call: Phone,
    call: Phone,
    meeting: Calendar,
    email: Mail
  };

  type TimelineFilter = 'all' | 'email' | 'activity' | 'issue';
  let filter = $state<TimelineFilter>('all');
  const filtered = $derived.by(() => {
    switch (filter) {
      case 'issue':
        return entries.filter((e) => e.kind === 'issue');
      case 'email':
        return entries.filter((e) => e.kind === 'activity' && e.activity.type === 'email');
      case 'activity':
        return entries.filter((e) => e.kind === 'activity' && e.activity.type !== 'email');
      default:
        return entries;
    }
  });

  function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'now';
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString();
  }

  function openSite(e: MouseEvent | KeyboardEvent, siteId: number) {
    e.stopPropagation();
    onOpenSite?.(siteId);
  }
</script>

{#snippet siteChip(siteId: number | null | undefined, siteName: string | null | undefined)}
  {#if showSiteChips && siteId && siteName}
    <button
      type="button"
      class="tym-badge inline-flex shrink-0 items-center gap-1"
      style="background:#E6E9EE; color:#10223A"
      onclick={(e) => openSite(e, siteId)}
    >
      <MapPin class="h-3 w-3" />{siteName}
    </button>
  {/if}
{/snippet}

<div class="mb-0.5 flex flex-wrap items-center gap-2">
  <div class="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">
    <Kanban class="h-3.5 w-3.5" /> Timeline
  </div>
  <span class="flex-1"></span>
  <div class="flex flex-wrap items-center gap-1.5">
    <button type="button" class="tym-chip !min-h-0 !py-1 {filter === 'all' ? 'active' : ''}" onclick={() => (filter = 'all')}>All</button>
    <button type="button" class="tym-chip !min-h-0 !py-1 {filter === 'email' ? 'active' : ''}" onclick={() => (filter = 'email')}>Emails</button>
    <button type="button" class="tym-chip !min-h-0 !py-1 {filter === 'activity' ? 'active' : ''}" onclick={() => (filter = 'activity')}>Activities</button>
    <button type="button" class="tym-chip !min-h-0 !py-1 {filter === 'issue' ? 'active' : ''}" onclick={() => (filter = 'issue')}>Issues</button>
  </div>
</div>

{#each filtered as entry (entry.kind === 'activity' ? `a:${entry.activity.id}` : `i:${entry.issue.id}`)}
  {#if entry.kind === 'issue'}
    {@const ib = ISSUE_STATUS_BADGE[entry.issue.status] ?? ISSUE_STATUS_BADGE.open}
    <div
      class="tym-card flex w-full min-w-0 items-start gap-2.5 px-4 py-3 text-left"
      role="button"
      tabindex="0"
      onclick={() => onOpenIssue(entry.issue.id)}
      onkeydown={(e) => e.key === 'Enter' && onOpenIssue(entry.issue.id)}
    >
      <Wrench class="mt-0.5 h-4 w-4 shrink-0" style="color:{ib.fg}" />
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="text-[11px] font-bold uppercase tracking-[.04em]" style="color:{ib.fg}">Issue</span>
          {@render siteChip(entry.issue.site_id, entry.issue.site_name)}
          <span class="flex-1"></span>
          <span class="shrink-0 text-[11px] text-dark-gray">{timeAgo(entry.issue.created_at)}</span>
        </div>
        <div class="mt-1 truncate text-[13.5px] font-medium text-toyama-heavy-carbon">{entry.issue.title}</div>
      </div>
    </div>
  {:else}
    {@const a = entry.activity}
    {@const ab = ACTIVITY_BADGE[a.type] ?? ACTIVITY_BADGE.note}
    {@const Icon = ACTIVITY_ICON[a.type] ?? FileText}
    {@const isEmail = a.type === 'email'}
    {#snippet activityBody()}
      <Icon class="mt-0.5 h-4 w-4 shrink-0" style="color:{ab.fg}" />
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="text-[11px] font-bold uppercase tracking-[.04em]" style="color:{ab.fg}">{a.type.replace('_', ' ')}</span>
          {@render siteChip(a.site_id, a.site_name)}
          <span class="flex-1"></span>
          <span class="shrink-0 text-[11px] text-dark-gray">{timeAgo(a.email?.sent_at ?? a.created_at)}</span>
        </div>
        {#if a.title}<div class="mt-1 truncate text-[13.5px] font-medium text-toyama-heavy-carbon">{a.title}</div>{/if}
        {#if isEmail && a.email}
          <div class="mt-1 flex min-w-0 items-center gap-1 text-[11.5px] text-dark-gray">
            {#if emailDirection(a) === 'received'}
              <ArrowDownLeft class="h-3 w-3 shrink-0" />
            {:else}
              <ArrowUpRight class="h-3 w-3 shrink-0" />
            {/if}
            <span class="truncate">{a.email.from_addr}</span>
            <span class="shrink-0 text-dark-gray/70">&middot; via {a.created_by}</span>
          </div>
          {#if a.email.snippet}<p class="mt-1 line-clamp-2 text-[12.5px] text-[#666]">{cleanEmailPreview(a.email.snippet)}</p>{/if}
        {:else}
          {#if a.body}<p class="mt-1 line-clamp-2 whitespace-pre-wrap text-[12.5px] text-[#666]">{a.body}</p>{/if}
          <div class="mt-1.5 text-[11px] text-dark-gray">{a.created_by}</div>
        {/if}
      </div>
    {/snippet}
    {#if isEmail}
      <!-- Its own branch (not a ternary'd role/tabindex on one element) so a
           static analyzer can tell this one's always interactive — see the
           non-email branch just below, which is never interactive at all. -->
      <div
        class="tym-card flex w-full min-w-0 items-start gap-2.5 px-4 py-3 text-left"
        role="button"
        tabindex="0"
        onclick={() => onOpenEmail(a)}
        onkeydown={(e) => e.key === 'Enter' && onOpenEmail(a)}
      >
        {@render activityBody()}
      </div>
    {:else}
      <div class="tym-card flex w-full min-w-0 items-start gap-2.5 px-4 py-3 text-left">
        {@render activityBody()}
      </div>
    {/if}
  {/if}
{:else}
  <p class="text-[12.5px] text-dark-gray">{emptyLabel}</p>
{/each}
